import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { CheckCircle2, ArrowRight, Link2 } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does "joining" two tables mean?',
    options: [
      'Copying one table below the other',
      'Combining them by matching rows using a shared key',
      'Merging all columns into one column',
      'Deleting duplicate rows from both tables',
    ],
    correctIndex: 1,
    explanation:
      'Joining means combining two tables by finding matching rows through a shared key. For each row in Table A, you find the matching row in Table B and bring the columns together.',
  },
  {
    question: 'What is the shared key between our Clicks and Conversions data?',
    options: [
      'Offer ID only',
      'Date only',
      'Offer ID + Date',
      'Clicks + Conversions',
    ],
    correctIndex: 2,
    explanation:
      'A single offer has data across many dates, and a single date has data for many offers. You need both Offer ID AND Date together to uniquely identify each row.',
  },
  {
    question: 'Why do we concatenate Offer ID and Date for VLOOKUP?',
    options: [
      'It makes the formula shorter',
      'Because VLOOKUP can only search one column, so we combine the key into one value',
      'Google Sheets requires it',
      'It is faster than using two separate lookups',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP searches a single column. Since our key is two fields (Offer ID + Date), we concatenate them into one value like "offer_1232024-01-15" so VLOOKUP can search on a single combined column.',
  },
  {
    question: 'After building the combined dataset, how do you get data for just one offer?',
    options: [
      'Delete the other rows',
      'Use FILTER or QUERY',
      'Create a new spreadsheet',
      'Use VLOOKUP again',
    ],
    correctIndex: 1,
    explanation:
      'Once you have the combined dataset, FILTER or QUERY can extract just the rows for a specific offer. The combined dataset stays intact as your single source of truth.',
  },
  {
    question: 'What goes in the Conversion Rate column?',
    options: [
      '=Clicks/Conversions',
      '=Conversions*Clicks',
      '=IFERROR(Conversions/Clicks, 0)',
      '=SUM(Conversions, Clicks)',
    ],
    correctIndex: 2,
    explanation:
      'Conversion Rate = Conversions divided by Clicks. We wrap it in IFERROR to handle cases where Clicks is 0 (which would cause a division error).',
  },
  {
    question: 'Why is a composite key needed instead of just Offer ID?',
    options: [
      'Google Sheets does not support single-column lookups',
      'Because each offer appears on multiple dates, so Offer ID alone is not unique',
      'Composite keys are always faster than single keys',
      'It prevents the spreadsheet from crashing',
    ],
    correctIndex: 1,
    explanation:
      'An offer like offer_123 has a row for each date. If you look up by Offer ID alone, VLOOKUP would only find the first match. You need Offer ID + Date to uniquely identify each row.',
  },
  {
    question: 'What does the curly brace array syntax { A:A&B:B, C:C } create?',
    options: [
      'A named range',
      'A virtual two-column array for the lookup',
      'A chart from the data',
      'A pivot table',
    ],
    correctIndex: 1,
    explanation:
      'The curly brace syntax builds an array on the fly. { ConvData!A:A&ConvData!B:B, ConvData!C:C } creates a virtual table where column 1 is the concatenated key and column 2 is the conversions value.',
  },
  {
    question: 'Which table should you start with as the base when building a combined dataset?',
    options: [
      'Always the smaller table',
      'The table with the most columns',
      'The table that has all the rows you need',
      'It does not matter — pick either one',
    ],
    correctIndex: 2,
    explanation:
      'Start with the table that contains all the row combinations you need. In our case, the Clicks table has one row per Offer ID + Date, so it is the natural base to build on.',
  },
  {
    question: 'What happens if a VLOOKUP cannot find a matching row in the Conversions table?',
    options: [
      'It returns 0 automatically',
      'It returns #N/A',
      'It skips that row entirely',
      'It returns the last value in the column',
    ],
    correctIndex: 1,
    explanation:
      'When VLOOKUP cannot find a match, it returns a #N/A error. This is why wrapping lookups in IFERROR is good practice — it lets you handle missing data gracefully.',
  },
  {
    question: 'What is the difference between INDEX/MATCH and VLOOKUP for joins?',
    options: [
      'INDEX/MATCH is newer and completely different',
      'They produce different results',
      'INDEX/MATCH can look left and is more flexible, but both achieve the same join',
      'VLOOKUP is more accurate than INDEX/MATCH',
    ],
    correctIndex: 2,
    explanation:
      'Both can perform the same join. INDEX/MATCH is more flexible because it can look up values in any direction and the lookup column does not need to be first. For our use case, both work equally well.',
  },
  {
    question: 'Why is the combined dataset called a "single source of truth"?',
    options: [
      'Because it is the only sheet in the workbook',
      'Because all reports pull from this one table instead of separate sheets',
      'Because Google Sheets labels it as the primary sheet',
      'Because it cannot be edited after creation',
    ],
    correctIndex: 1,
    explanation:
      'A single source of truth means one authoritative table that all reports and analysis draw from. Instead of each report pulling from different sheets and potentially getting different numbers, everything comes from one combined dataset.',
  },
  {
    question: 'If you accidentally use Offer ID alone as the lookup key, what would go wrong?',
    options: [
      'The formula would return an error for every row',
      'You would get the same conversion value for every date of that offer',
      'The spreadsheet would freeze',
      'Nothing — it would still work correctly',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP returns the first match it finds. If you search by Offer ID alone, every row for offer_123 would get the conversions from the first date found, giving you incorrect data for all other dates.',
  },
]

/* ─── Demo data ─── */
const CLICKS_DATA = [
  { offer: 'offer_101', date: '2024-01-15', clicks: 340 },
  { offer: 'offer_123', date: '2024-01-15', clicks: 210 },
  { offer: 'offer_101', date: '2024-01-16', clicks: 290 },
  { offer: 'offer_123', date: '2024-01-16', clicks: 185 },
]

const CONVERSIONS_DATA = [
  { offer: 'offer_101', date: '2024-01-15', conversions: 12 },
  { offer: 'offer_123', date: '2024-01-15', conversions: 8 },
  { offer: 'offer_101', date: '2024-01-16', conversions: 15 },
  { offer: 'offer_123', date: '2024-01-16', conversions: 6 },
]

const COMBINED_DATA = CLICKS_DATA.map((c) => {
  const conv = CONVERSIONS_DATA.find((v) => v.offer === c.offer && v.date === c.date)
  return {
    ...c,
    conversions: conv ? conv.conversions : 0,
    cr: conv && c.clicks > 0 ? ((conv.conversions / c.clicks) * 100).toFixed(1) : '0.0',
  }
})

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Join Animation ─── */
function JoinAnimation() {
  const [activeRow, setActiveRow] = useState(null)

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">Hover over a clicks row to see it match with the conversions table.</p>
      <div className="grid gap-4 md:grid-cols-3">
        {/* Clicks table */}
        <div>
          <p className="text-xs font-medium text-blue-600 mb-2">Clicks Table</p>
          <div className="overflow-hidden rounded-lg border border-blue-200 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-blue-50">
                  <th className="border-b border-r border-blue-200 px-2 py-1.5 text-xs text-blue-700 font-medium">Offer</th>
                  <th className="border-b border-r border-blue-200 px-2 py-1.5 text-xs text-blue-700 font-medium">Date</th>
                  <th className="border-b border-blue-200 px-2 py-1.5 text-xs text-blue-700 font-medium">Clicks</th>
                </tr>
              </thead>
              <tbody>
                {CLICKS_DATA.map((row, i) => (
                  <tr
                    key={i}
                    onMouseEnter={() => setActiveRow(i)}
                    onMouseLeave={() => setActiveRow(null)}
                    className={`cursor-pointer transition-colors ${activeRow === i ? 'bg-blue-100' : 'hover:bg-blue-50/50'}`}
                  >
                    <td className="border-b border-r border-blue-100 px-2 py-1.5 font-mono text-[11px]">{row.offer}</td>
                    <td className="border-b border-r border-blue-100 px-2 py-1.5 text-[11px]">{row.date}</td>
                    <td className="border-b border-blue-100 px-2 py-1.5 text-[11px] font-semibold">{row.clicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Arrow */}
        <div className="flex items-center justify-center">
          <motion.div
            animate={{ scale: activeRow !== null ? 1.2 : 1 }}
            className="flex flex-col items-center gap-2"
          >
            <Link2 className={`w-6 h-6 transition-colors ${activeRow !== null ? 'text-emerald-500' : 'text-gray-300'}`} />
            <p className="text-xs text-gray-400 text-center">Match on<br />Offer + Date</p>
            <ArrowRight className={`w-6 h-6 transition-colors ${activeRow !== null ? 'text-emerald-500' : 'text-gray-300'}`} />
          </motion.div>
        </div>

        {/* Conversions table */}
        <div>
          <p className="text-xs font-medium text-green-600 mb-2">Conversions Table</p>
          <div className="overflow-hidden rounded-lg border border-green-200 shadow-sm">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-green-50">
                  <th className="border-b border-r border-green-200 px-2 py-1.5 text-xs text-green-700 font-medium">Offer</th>
                  <th className="border-b border-r border-green-200 px-2 py-1.5 text-xs text-green-700 font-medium">Date</th>
                  <th className="border-b border-green-200 px-2 py-1.5 text-xs text-green-700 font-medium">Conv</th>
                </tr>
              </thead>
              <tbody>
                {CONVERSIONS_DATA.map((row, i) => (
                  <tr
                    key={i}
                    className={`transition-colors ${activeRow === i ? 'bg-green-100' : ''}`}
                  >
                    <td className="border-b border-r border-green-100 px-2 py-1.5 font-mono text-[11px]">{row.offer}</td>
                    <td className="border-b border-r border-green-100 px-2 py-1.5 text-[11px]">{row.date}</td>
                    <td className="border-b border-green-100 px-2 py-1.5 text-[11px] font-semibold">{row.conversions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module9() {
  return (
    <ModuleLayout moduleId={11}>
      <div className="space-y-8">
        {/* Section 1: The Goal */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Goal</h2>
              <p className="text-lg text-gray-600">
                We have two separate datasets. We need to combine them into one table.
              </p>
            </div>

            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="rounded-lg border-2 border-blue-200 bg-blue-50 p-4"
                >
                  <p className="text-xs font-bold text-blue-700 mb-1">Clicks Sheet</p>
                  <p className="text-sm text-blue-600">Offer ID + Date + Clicks</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="rounded-lg border-2 border-green-200 bg-green-50 p-4"
                >
                  <p className="text-xs font-bold text-green-700 mb-1">Conversions Sheet</p>
                  <p className="text-sm text-green-600">Offer ID + Date + Conversions</p>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex justify-center"
              >
                <div className="rounded-lg bg-gray-100 px-4 py-2 text-gray-400 text-2xl">
                  &#8595;
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 text-center"
              >
                <p className="text-xs font-bold text-emerald-700 mb-1">Combined Dataset</p>
                <p className="text-sm text-emerald-600">Date + Offer ID + Clicks + Conversions + Conversion Rate</p>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* Section 2: The Join Concept */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Join Concept</h2>
              <p className="text-lg text-gray-600">
                A join means: for each row in Table A, find the matching row in Table B using the shared key.
              </p>
            </div>

            <JoinAnimation />
          </div>
        </Section>

        {/* Section 3: Building the Combined Dataset */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Building the Combined Dataset</h2>
              <p className="text-lg text-gray-600">
                Step by step, here is how you build it.
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  num: 1,
                  title: 'Start with the Clicks table as your base',
                  detail: 'It has all the rows you need — one per Offer ID + Date combination.',
                  color: 'blue',
                },
                {
                  num: 2,
                  title: 'Add a "Conversions" column',
                  detail: 'In a new column next to Clicks, use VLOOKUP or INDEX/MATCH to pull conversions from the Conversions sheet.',
                  color: 'green',
                },
                {
                  num: 3,
                  title: 'Use a composite key for the lookup',
                  detail: 'Since the key is two fields, concatenate them: A2&B2 creates "offer_1232024-01-15".',
                  color: 'purple',
                },
                {
                  num: 4,
                  title: 'Add a Conversion Rate column',
                  detail: '=IFERROR(Conversions/Clicks, 0) — handles the divide-by-zero case.',
                  color: 'amber',
                },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`flex items-start gap-4 rounded-xl border border-${step.color}-200 bg-${step.color}-50 p-5`}
                >
                  <span className={`flex items-center justify-center w-8 h-8 rounded-full bg-${step.color}-200 text-${step.color}-800 font-bold text-sm shrink-0`}>
                    {step.num}
                  </span>
                  <div>
                    <h4 className={`font-bold text-${step.color}-800`}>{step.title}</h4>
                    <p className={`text-sm text-${step.color}-700 mt-1`}>{step.detail}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">The VLOOKUP formula for the Conversions column:</p>
              <div className="rounded-lg bg-gray-900 p-4 overflow-x-auto">
                <code className="text-sm text-emerald-400 font-mono whitespace-nowrap">
                  =VLOOKUP(A2&B2, {'{'} ConvData!A:A&ConvData!B:B, ConvData!C:C {'}'}, 2, FALSE)
                </code>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-2">
                <p className="text-sm text-blue-800">
                  <strong>The concatenation trick:</strong> A2&B2 joins Offer ID and Date into one string like "offer_1232024-01-15".
                </p>
                <p className="text-sm text-blue-700">
                  The array {'{'} ConvData!A:A&ConvData!B:B, ConvData!C:C {'}'} creates a virtual two-column lookup table where the first column is the same concatenated key.
                </p>
              </div>

              <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                <p className="text-sm text-purple-800 mb-2"><strong>Alternative: INDEX/MATCH with two conditions</strong></p>
                <div className="rounded-lg bg-gray-900 px-3 py-2">
                  <code className="text-xs text-purple-400 font-mono">
                    =INDEX(ConvData!C:C, MATCH(A2&B2, ConvData!A:A&ConvData!B:B, 0))
                  </code>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Section 4: The Result */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Result</h2>
              <p className="text-lg text-gray-600">
                Your beautiful combined table — the single source of truth.
              </p>
            </div>

            <div className="overflow-hidden rounded-lg border-2 border-emerald-300 shadow-sm">
              <div className="bg-emerald-600 text-white text-center py-2 text-sm font-bold">
                Combined Dataset
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-emerald-50">
                    <th className="border-b border-r border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-medium">Offer ID</th>
                    <th className="border-b border-r border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-medium">Date</th>
                    <th className="border-b border-r border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-medium">Clicks</th>
                    <th className="border-b border-r border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-medium">Conversions</th>
                    <th className="border-b border-emerald-200 px-3 py-2 text-xs text-emerald-700 font-medium">CR%</th>
                  </tr>
                </thead>
                <tbody>
                  {COMBINED_DATA.map((row, i) => (
                    <motion.tr
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                    >
                      <td className="border-b border-r border-emerald-100 px-3 py-1.5 font-mono text-xs">{row.offer}</td>
                      <td className="border-b border-r border-emerald-100 px-3 py-1.5 text-xs">{row.date}</td>
                      <td className="border-b border-r border-emerald-100 px-3 py-1.5 text-xs font-semibold">{row.clicks}</td>
                      <td className="border-b border-r border-emerald-100 px-3 py-1.5 text-xs font-semibold">{row.conversions}</td>
                      <td className="border-b border-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">{row.cr}%</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center"
            >
              <p className="text-emerald-800 font-semibold text-lg">
                This is your single source of truth.
              </p>
              <p className="text-sm text-emerald-600 mt-1">
                Every report you build starts here. From this one table, you can FILTER or QUERY to get any offer's data.
              </p>
            </motion.div>
          </div>
        </Section>

        {/* Section 5: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={11} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
